import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { getSensors } from "../unitEventsUtils";
import {
  getScopedSensorAlarms,
  getScopedSensorSessions,
  getScopedSensorConnectivity,
  liveRows,
} from "../scopedTelemetry";
import { useSensorTenures } from "./useSensorTenure";
import {
  currentTenureIsOwned,
  scopeKeyOf,
  TENURE_UNRESOLVED,
  type TenureScope,
} from "../../../services/installationTenure";
import {
  DEFAULT_SECTIONS,
  buildBuildingReport,
  weeklyWindow,
  type BuildingReport,
  type UnitInput,
} from "../buildingReport";

/**
 * Fetches everything one property's weekly report needs, and hands it to the
 * pure model.
 *
 * Nothing here decides what a number means; that is `buildingReport`, which is
 * held to the same golden fixture as the emailed report. This file only fans
 * out the reads.
 *
 * Query keys and cache timings deliberately match `useBuildingAggregates`, so
 * arriving here from the portfolio page is a cache hit rather than a second
 * fan-out over the same sensors. Each key carries the sensor's location-scope
 * key too; see `scopeKeyOf` for why the scope has to be in the key and not only
 * in the fetch.
 */

const STALE_TIME = 10 * 60_000; // 10m
const GC_TIME = 2 * 60 * 60_000; // 2h
const REFETCH_INTERVAL = 60_000 * 60; // 1h

/** Matches useBuildingAggregates: one fetch per sensor, filtered client-side. */
const ALERT_PAGE_SIZE = 5000;

/**
 * Connectivity rows to pull per sensor.
 *
 * The endpoint takes a page size and no date filter, so this has to be large
 * enough to reach back past the start of the comparison period. A sensor that
 * flaps produces a row per transition; 500 covers a fortnight of a badly
 * behaved unit, and anything beyond that only costs the report the earliest
 * transitions, which is why an unknown starting state fails closed rather than
 * reading as online.
 */
const CONNECTIVITY_PAGE_SIZE = 500;

const SESSION_PAGE_SIZE = 2000;

export type BuildingReportResult = {
  report: BuildingReport | null;
  status: "loading" | "ready" | "error";
  /**
   * Sensors on this report whose figures are NOT scoped to one location this
   * organization owns. Two different ways that happens, both of which leave the
   * totals carrying periods they should not:
   *
   *   - no install record at all, so there is no cutoff and the figures cover
   *     the sensor's whole recorded history;
   *   - a cutoff that exists but lands on a tenure we cannot attribute to this
   *     organization, because a transfer by group reassignment writes no
   *     installation row and the newest one on file is the previous owner's.
   *
   * The page names the count rather than letting the totals read as clean.
   */
  unscopedSensors: string[];
};

type Sensor = { name?: string; displayName?: string };

/** ISO instant the sessions endpoint accepts as a bound. */
function isoBound(ms: number): string {
  return new Date(ms).toISOString().slice(0, 19);
}

export function useBuildingReport(
  buildingId: string,
  buildingName: string,
  now?: Date,
  /** Every property this organization owns, for deciding whether a sensor's
   *  current tenure is one of them. Empty means nothing can be attributed, so
   *  every sensor is reported as unscoped -- see `unscopedSensors`. */
  ownedGroupNames: string[] = []
): BuildingReportResult {
  // Recomputed only when the day changes, so a render does not silently move
  // the reporting period under the reader.
  const window = useMemo(
    () => weeklyWindow(now),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [now ? now.getTime() : new Date().toDateString()]
  );

  const sensorsQuery = useQueries({
    queries: [
      {
        queryKey: ["sensors", buildingId],
        queryFn: async () => await getSensors(buildingId),
        enabled: !!buildingId,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        refetchOnWindowFocus: false,
        refetchInterval: REFETCH_INTERVAL,
      },
    ],
  })[0];

  const sensors: Sensor[] = sensorsQuery?.data?.data ?? [];
  const names = sensors.map((s) => s?.name).filter(Boolean) as string[];

  // One installations read per sensor, shared with every other screen. The three
  // fan-outs below are gated on it, so no read can land in a scoped cache key
  // carrying unscoped rows.
  const tenures = useSensorTenures(names);
  const scopeOf = (name: string): TenureScope =>
    tenures.get(name)?.scope ?? TENURE_UNRESOLVED;
  const settledFor = (name: string): boolean =>
    tenures.get(name)?.settled ?? false;

  const alarmQueries = useQueries({
    queries: names.map((name) => ({
      queryKey: ["alerts-agg", name, scopeKeyOf(scopeOf(name))],
      queryFn: async () =>
        await getScopedSensorAlarms(name, ALERT_PAGE_SIZE, scopeOf(name)),
      enabled: settledFor(name),
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      refetchInterval: REFETCH_INTERVAL,
    })),
  });

  // Both periods in one fetch, bounded at the start of the comparison window,
  // so the comparison costs one read per sensor rather than two.
  const sessionQueries = useQueries({
    queries: names.map((name) => ({
      queryKey: [
        "report-sessions",
        name,
        window.priorStart,
        window.end,
        scopeKeyOf(scopeOf(name)),
      ],
      queryFn: async () =>
        await getScopedSensorSessions(
          name,
          SESSION_PAGE_SIZE,
          isoBound(window.priorStart),
          isoBound(window.end),
          scopeOf(name)
        ),
      enabled: settledFor(name),
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      refetchInterval: REFETCH_INTERVAL,
    })),
  });

  const connectivityQueries = useQueries({
    queries: names.map((name) => ({
      queryKey: ["report-connectivity", name, scopeKeyOf(scopeOf(name))],
      queryFn: async () =>
        await getScopedSensorConnectivity(
          name,
          CONNECTIVITY_PAGE_SIZE,
          scopeOf(name)
        ),
      enabled: settledFor(name),
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      refetchInterval: REFETCH_INTERVAL,
    })),
  });

  // Cutoffs move (a tenure resolves, a sensor is re-homed) and every figure
  // below depends on them, so they belong in the memo's signature.
  const ownedSignature = ownedGroupNames.join("|");
  const tenureSignature = names
    .map((name) => `${name}:${scopeKeyOf(scopeOf(name))}:${settledFor(name)}`)
    .join("|");

  const signature = [
    sensorsQuery?.status,
    sensorsQuery?.dataUpdatedAt,
    ...alarmQueries.map((q) => `${q.status}:${q.dataUpdatedAt}`),
    ...sessionQueries.map((q) => `${q.status}:${q.dataUpdatedAt}`),
    ...connectivityQueries.map((q) => `${q.status}:${q.dataUpdatedAt}`),
  ].join("|");

  return useMemo<BuildingReportResult>(() => {
    // No cutoff at all, OR a cutoff on a tenure we cannot attribute to this
    // organization. Both leave the sensor's figures covering a period the
    // totals should not claim; see the type for why they are one list.
    const unscopedSensors = names.filter((name) => {
      const scope = scopeOf(name);
      return (
        scope.cutoffMs == null || !currentTenureIsOwned(scope, ownedGroupNames)
      );
    });

    if (sensorsQuery?.status === "error")
      return { report: null, status: "error", unscopedSensors };
    if (sensorsQuery?.status !== "success")
      return { report: null, status: "loading", unscopedSensors };

    // Tenures gate the three fan-outs, so an unsettled one shows up as a pending
    // query below -- but check it directly too, since a sensor whose tenure has
    // not arrived has no queries at all rather than pending ones.
    if (names.some((name) => !settledFor(name)))
      return { report: null, status: "loading", unscopedSensors };

    const pending = [...alarmQueries, ...sessionQueries, ...connectivityQueries]
      .some((q) => q.status === "pending");
    if (pending) return { report: null, status: "loading", unscopedSensors };

    const units: UnitInput[] = names.map((name, index) => {
      const sensor = sensors[index] || {};
      const alarms = alarmQueries[index]?.data?.data ?? [];
      const sessions = sessionQueries[index]?.data?.data ?? [];

      // A failed connectivity read is NOT an empty one. Empty would say "no
      // record", which the model reads as unknown and reports honestly; a
      // failure that arrived as an empty array would look the same as a sensor
      // that has never reported, and both would be wrong for this sensor.
      const connectivity =
        connectivityQueries[index]?.status === "success"
          ? connectivityQueries[index]?.data?.data ?? []
          : null;
      // Unclipped, and only for the "online now" claim -- see UnitInput. A
      // sensor installed here an hour ago has no scoped connectivity rows yet,
      // and reporting it as offline would be a false alarm.
      const connectivityLive =
        connectivityQueries[index]?.status === "success"
          ? liveRows(connectivityQueries[index]?.data)
          : null;

      // No comparable baseline: the comparison week ended before this sensor was
      // installed here, so a "previously" figure would describe another kitchen.
      // The model omits the comparison rather than reporting a fall to zero.
      const cutoff = scopeOf(name).cutoffMs;
      const comparable = cutoff == null || cutoff <= window.priorStart;

      return {
        scsName: name,
        name: sensor.displayName || name,
        alarms,
        sessions,
        connectivity,
        connectivityLive,
        // The same rows serve both periods: the model filters each by window --
        // unless this sensor has not been here long enough to have a baseline.
        prior: comparable ? { alarms, sessions } : null,
      };
    });

    return {
      report: buildBuildingReport(
        buildingId,
        buildingName,
        units,
        window,
        DEFAULT_SECTIONS
      ),
      status: "ready",
      unscopedSensors,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    signature,
    buildingId,
    buildingName,
    window,
    tenureSignature,
    ownedSignature,
  ]);
}
